// trace: Studio SPA bootstrap.
// Inputs: hosting page #root element.
// Outputs: mounted React tree (Router + QueryClient + Auth + i18n).
// Reach: browser hydrates the route tree; auth context fetches /api/auth/session.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import './i18n/index.js';
import { query_client } from './lib/query.js';
import { AuthProvider } from './auth.js';
import { OfflineProvider } from './offline/context.js';
import { App } from './app.js';
import { boot_accent } from './lib/theme.js';
import './styles/index.css';

// Apply the tenant's accent (cached choice, else forest default) before first
// render so there's no maroon→forest flash.
boot_accent();

const root_element = document.getElementById('root');
if (!root_element) {
  throw new Error('Studio bootstrap failed: #root not found');
}

createRoot(root_element).render(
  <StrictMode>
    <QueryClientProvider client={query_client}>
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <App />
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

// Register the service worker for offline shell caching. The mutation queue
// is JS-layer (see src/offline/queue.ts) so this SW only handles assets.
//
// The browser auto-checks sw.js on full navigations, but SPA route changes
// don't trigger that check. We add a visibilitychange handler so foremen
// who keep a tab open all day still pick up new deploys when they return
// to the tab (e.g. after lunch). The CACHE_VERSION embedded in sw.js is
// auto-bumped per build (see vite.config.ts), so a byte-different sw.js
// triggers the install/activate lifecycle which clears the old shell cache.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            void reg.update().catch(() => undefined);
          }
        });
      })
      .catch(() => undefined);
  });
}
